import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, ShieldCheck, MailCheck } from "lucide-react";
import logo from "@/assets/galahadji-logo.png";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [rememberDevice, setRememberDevice] = useState(true);
  const [needCode, setNeedCode] = useState(false);
  const [emailFallback, setEmailFallback] = useState(false);
  const [emailNote, setEmailNote] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login"); // login | forgot
  const [forgotSent, setForgotSent] = useState(false);
  const [lockUntil, setLockUntil] = useState(() => {
    const v = Number(localStorage.getItem("gj_login_lock_until") || 0);
    return v > Date.now() ? v : 0;
  });
  const [now, setNow] = useState(Date.now());
  const locked = lockUntil > now;
  const lockRemaining = Math.max(0, Math.ceil((lockUntil - now) / 1000));

  useEffect(() => {
    if (!locked) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [locked]);
  useEffect(() => {
    if (lockUntil && lockUntil <= now) {
      localStorage.removeItem("gj_login_lock_until");
      setLockUntil(0);
    }
  }, [now, lockUntil]);

  const lockNotice = () => {
    const m = Math.floor(lockRemaining / 60);
    const s = lockRemaining % 60;
    return `Too many unsuccessful login attempts. Please try again in ${m} minute${m === 1 ? "" : "s"} and ${s} second${s === 1 ? "" : "s"}.`;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (locked) return;
    setError(""); setLoading(true);
    try {
      const res = await login(email, password, needCode ? code : undefined, needCode ? rememberDevice : false);
      if (res && res.two_factor_required) {
        setNeedCode(true);
        setEmailFallback(!!res.email_fallback);
        setLoading(false);
        return;
      }
      nav("/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 429 && detail && typeof detail === "object" && detail.retry_after) {
        const until = Date.now() + Number(detail.retry_after) * 1000;
        localStorage.setItem("gj_login_lock_until", String(until));
        setLockUntil(until); setNow(Date.now());
        setError("");
      } else {
        setError(formatApiError(detail) || err.message);
      }
      setLoading(false);
    }
  };

  const submitForgot = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const backToLogin = () => { setMode("login"); setForgotSent(false); setError(""); };

  const requestEmailCode = async () => {
    setError(""); setEmailNote("");
    try {
      await api.post("/auth/2fa/email-code", { email, password });
      setEmailNote("If your account has email codes enabled, we've sent a 6-digit code to your inbox. It expires in 10 minutes.");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:flex relative bg-primary text-primary-foreground grain overflow-hidden flex-col justify-between p-12">
        <Link to="/" className="relative z-10"><img src={logo} alt="Galahadji Acquisitions" className="h-40 w-auto object-contain" /></Link>
        <div className="relative z-10">
          <h2 className="font-marketing text-4xl font-bold leading-tight">Acquisition intelligence,<br /><span className="italic text-accent">under one command center.</span></h2>
          <p className="text-white/60 mt-4 max-w-md">Leads, underwriting, offers, and closings — orchestrated end to end for the Galahadji team.</p>
        </div>
        <div className="relative z-10 text-white/40 text-xs uppercase tracking-[0.2em]">Secure Team Access</div>
      </div>

      <div className="flex items-center justify-center p-8">
        {mode === "forgot" ? (
          <form onSubmit={submitForgot} className="w-full max-w-sm" data-testid="forgot-form">
            <div className="mb-8 lg:hidden"><img src={logo} alt="Galahadji Acquisitions" className="h-24 w-auto object-contain" /></div>
            <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2"><Lock className="w-3.5 h-3.5" /> Password Reset</div>
            <h1 className="font-display text-3xl font-bold tracking-tight mb-3">Forgot password?</h1>

            {error && <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-4 py-3 mb-4" data-testid="forgot-error">{error}</div>}

            {forgotSent ? (
              <div className="space-y-5" data-testid="forgot-success">
                <div className="flex items-start gap-3 bg-secondary rounded-md p-4">
                  <MailCheck className="w-5 h-5 text-accent mt-0.5 shrink-0" />
                  <p className="text-sm text-muted-foreground">If an account exists for <span className="text-foreground font-medium">{email}</span>, we've sent a link to reset your password. Check your inbox (and spam) — the link expires in 1 hour.</p>
                </div>
                <Button type="button" variant="outline" className="w-full h-11" onClick={backToLogin} data-testid="forgot-back">Back to sign in</Button>
              </div>
            ) : (
              <>
                <p className="text-sm text-muted-foreground mb-6">Enter the email on your account and we'll send you a secure link to set a new password.</p>
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" required />
                </div>
                <Button type="submit" disabled={loading} className="w-full mt-6 h-11" data-testid="forgot-submit">{loading ? "Sending…" : "Send reset link"}</Button>
                <button type="button" onClick={backToLogin} className="w-full text-center text-sm text-muted-foreground mt-6 underline underline-offset-4 hover:text-foreground" data-testid="forgot-back-link">Back to sign in</button>
              </>
            )}
          </form>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="mb-8 lg:hidden"><img src={logo} alt="Galahadji Acquisitions" className="h-24 w-auto object-contain" /></div>
          <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2"><Lock className="w-3.5 h-3.5" /> Team Login</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mb-8">Welcome back.</h1>

          {error && <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-4 py-3 mb-4" data-testid="login-error">{error}</div>}
          {locked && <div className="bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-500 text-sm rounded-md px-4 py-3 mb-4" data-testid="login-lockout-notice" role="status" aria-live="polite">{lockNotice()}</div>}

          {!needCode ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Email</Label>
                <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" required />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Password</Label>
                  <button type="button" onClick={() => { setMode("forgot"); setError(""); }} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="login-forgot-link">Forgot password?</button>
                </div>
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" required />
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="login-2fa-step">
              <div className="inline-flex items-center gap-2 text-sm text-foreground"><ShieldCheck className="w-4 h-4 text-accent" /> Two-factor verification</div>
              <p className="text-sm text-muted-foreground">Enter the 6-digit code from your authenticator app (or a backup code).</p>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-[0.15em] text-muted-foreground">Authentication Code</Label>
                <Input value={code} onChange={(e) => setCode(e.target.value)} data-testid="login-2fa-code" inputMode="numeric" autoFocus placeholder="123456" required />
              </div>
              <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
                <input type="checkbox" checked={rememberDevice} onChange={(e) => setRememberDevice(e.target.checked)} className="h-4 w-4 accent-[#d4a017]" data-testid="login-remember-device" />
                Trust this device for 30 days — don't ask for codes here again
              </label>
              {emailNote && <div className="bg-secondary text-muted-foreground text-xs rounded-md px-3 py-2" data-testid="login-2fa-email-note">{emailNote}</div>}
              {emailFallback && (
                <button type="button" onClick={requestEmailCode} className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2" data-testid="login-2fa-email-fallback">Can't use your authenticator? Email me a code instead</button>
              )}
            </div>
          )}
          <Button type="submit" disabled={loading || locked} data-testid="login-submit" className="w-full mt-6 h-11">{loading ? "Signing in…" : locked ? `Try again in ${lockRemaining}s` : needCode ? "Verify & Sign In" : "Sign In"}</Button>
          {!needCode && <p className="text-center text-sm text-muted-foreground mt-6">Are you a seller? <Link to="/seller-intake" className="text-foreground underline underline-offset-4">Submit your property</Link></p>}
        </form>
        )}
      </div>
    </div>
  );
}
